/**
 * @file useAuthEvents.js
 * @feature Auth
 * @description Registers and cleans up MSAL event callbacks.
 * Handles: login success, token acquisition, profile edit, forgot-password,
 * and login failures (AADB2C90118 = forgot-password trigger).
 *
 * FDD rationale: MSAL event wiring is a distinct auth behaviour.
 * Extracting it lets Pages stay declarative and eliminates the
 * ~70-line event-handler block from the monolithic useEffect.
 */

import { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';
import { b2cPolicies } from '../../authConfig';
import { compareIssuingPolicy } from '../../utils/claimUtils';
import { AUTH_SCOPES, FETCH_PROFILE_DELAY_MS } from '../../config/Auth.config';

/**
 * @param {Function} onTokenAcquired  – callback(idToken) on LOGIN_SUCCESS / ACQUIRE_TOKEN_SUCCESS
 */
export const useAuthEvents = (onTokenAcquired) => {
  const { instance } = useMsal();

  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      const account = instance.getActiveAccount();

      // Ignore events without an active account (except login itself)
      if (!account && event.eventType !== EventType.LOGIN_SUCCESS) return;

      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        event.payload?.account
      ) {
        const idToken =
          event.payload?.idToken ?? event.payload.idTokenClaims?.id_token;

        if (idToken) {
          sessionStorage.setItem('id_token', idToken);
          // Small delay ensures MSAL has settled its internal state
          setTimeout(() => onTokenAcquired(idToken), FETCH_PROFILE_DELAY_MS);
        }

        // Profile-edit flow: silently re-authenticate with sign-in policy
        if (compareIssuingPolicy(event.payload.idTokenClaims, b2cPolicies.names.editProfile)) {
          const originalAccount = instance
            .getAllAccounts()
            .find(
              (acc) =>
                acc.idTokenClaims.oid === event.payload.idTokenClaims.oid &&
                acc.idTokenClaims.sub === event.payload.idTokenClaims.sub &&
                compareIssuingPolicy(acc.idTokenClaims, b2cPolicies.names.signUpSignIn),
            );

          instance
            .ssoSilent({
              authority: b2cPolicies.authorities.signUpSignIn.authority,
              account: originalAccount,
            })
            .catch(() => {/* silent failure is acceptable here */});
        }

        // Forgot-password success → re-login with sign-in policy
        if (compareIssuingPolicy(event.payload.idTokenClaims, b2cPolicies.names.forgotPassword)) {
          instance.loginRedirect({
            authority: b2cPolicies.authorities.signUpSignIn.authority,
            scopes: AUTH_SCOPES,
          });
        }
      }

      // B2C forgot-password error (AADB2C90118)
      if (event.eventType === EventType.LOGIN_FAILURE) {
        if (event.error?.errorMessage?.includes('AADB2C90118')) {
          instance.loginRedirect({
            authority: b2cPolicies.authorities.forgotPassword.authority,
            scopes: [],
          });
        }
      }
    });

    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance, onTokenAcquired]);
};